import React, { FC, Fragment, useEffect } from 'react';
import { useCurrentProject } from '@material-bank/projects-utils';
import { useSelector, useDispatch } from 'react-redux';
import * as Styled from './AppContainer.styles';
import { BrandSnackbar } from '../../components/BrandSnackbar';
import { ErrorModal } from '../../components/modals/ErrorModal';
import { ExportModal } from '../../components/modals/ExportModal';
import { ProjectModalListener } from '../../components/modals/ProjectsModal';
import { Snackbar } from '../../components/Snackbar';
import { ColorPickerContainer } from '../../containers/ColorPickerContainer';
import FilterWrapper from '../../containers/FilterContainer/FilterWrapper';
import ResultsHeaderWrapper from '../../containers/ResultsHeader/ResultsHeaderWrapper';
import {
  isCompareTableShownSelector,
  isSearchLiteSelector,
} from '../../redux/selectors/commonSelector';
import { setIsCompareTableShown } from '../../redux/sliceCreators/commonSlice';
import {
  isBannersPage,
  isPartnersPage,
  shouldRenderCompareOnInit,
} from '../../utils/helpers';
import { CompareTableWrapper } from '../CompareTableWrapper/CompareTableWrapper';
import { FixedTopbar } from '../FixedTopbar';
import TopBannersWrapper from '../LaunchesContainer/TopBannersWrapper';
import { SearchFormListener } from '../SearchFormListener';
import { SearchResultsContainer } from '../SearchResultsContainer';
import { CMSModalContainer } from '../SearchResultsContainer/CMSModalContainer';

const isPartnersSearch = isPartnersPage();
const isBannersSearch = isBannersPage();
const shouldShowTopbar = !isPartnersSearch;
const shouldShowFilters = !isPartnersSearch && !isBannersSearch;

export type AppContainerProps = {
  displayAddAllToCartButton?: boolean;
  displayHeaderFilterButton?: boolean;
  displayHeaderFilters?: boolean;
  displayViewOptions?: boolean;
  displayContentBlocks?: boolean;
};

const AppContainer: FC<AppContainerProps> = ({
  displayAddAllToCartButton = false,
  displayHeaderFilterButton,
  displayHeaderFilters = false,
  displayViewOptions,
}) => {
  const isSearchLite = useSelector(isSearchLiteSelector);
  const isCompareTableShown = useSelector(isCompareTableShownSelector);
  const dispatch = useDispatch();

  const { setCurrentProjectId } = useCurrentProject();

  useEffect(() => {
    if (shouldRenderCompareOnInit()) {
      dispatch(setIsCompareTableShown(true));
    }
  }, []);

  const shouldShowColors =
    (!isSearchLite || isBannersSearch) && !isPartnersSearch;

  const handleNewProjectSelect = (e: CustomEvent) => {
    const newProjectId =
      e?.detail?.project?.externalId || e?.detail?.project?.project_id;

    if (newProjectId) {
      setCurrentProjectId(newProjectId);
    }
  };

  useEffect(() => {
    window.addEventListener('project-selected', handleNewProjectSelect);
    return () => {
      window.removeEventListener('project-selected', handleNewProjectSelect);
    };
  }, []);

  return (
    <div id="productSearchWrapper" style={{ width: '100%' }}>
      <Styled.AppWrapper>
        <SearchFormListener />
        {isCompareTableShown ? (
          <CompareTableWrapper />
        ) : (
          <Fragment>
            {!isSearchLite && shouldShowFilters && <TopBannersWrapper />}
            {shouldShowTopbar && (
              <FixedTopbar
                displayHeaderFilters={displayHeaderFilters || shouldShowFilters}
              >
                <ResultsHeaderWrapper
                  displayAddAllToCartButton={displayAddAllToCartButton}
                  displayHeaderFilterButton={displayHeaderFilterButton}
                  displayViewOptions={displayViewOptions}
                />
                {shouldShowColors && <ColorPickerContainer />}
              </FixedTopbar>
            )}
            {/* Do not remove this Id, it is important for miniPDP correct render */}
            <Styled.ResultsContainer id="searchResultsContainer">
              <FilterWrapper />
              <Styled.ResultsWrapper>
                <SearchResultsContainer />
              </Styled.ResultsWrapper>
            </Styled.ResultsContainer>
          </Fragment>
        )}
      </Styled.AppWrapper>
      <Snackbar />
      <BrandSnackbar />
      <ProjectModalListener />
      <ErrorModal dialogWidth={480} />
      <ExportModal />
      <CMSModalContainer />
    </div>
  );
};

export { AppContainer };
